// localStorage-backed "my default device" preference. Scoped to this
// device/browser so a shared workstation keeps whatever the last user set;
// New Encounter reads it to pre-select a device.
import { useCallback, useSyncExternalStore } from "react";

const STORAGE_KEY = "epocus.defaultDeviceId";
const SAME_TAB_EVENT = "epocus.defaultDeviceId.change";

export type DefaultDeviceId = string | null;

const read = (): DefaultDeviceId => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage blocked/disabled — behave as if no preference is set rather
    // than crashing render (read() runs inside useSyncExternalStore).
    return null;
  }
};

export const setDefaultDevice = (id: DefaultDeviceId): void => {
  try {
    if (id === null) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, id);
    }
  } catch {
    // Storage blocked/disabled — the preference just doesn't persist; the
    // event below makes subscribers re-read, so the UI stays truthful.
  }
  window.dispatchEvent(new CustomEvent(SAME_TAB_EVENT));
};

const subscribe = (listener: () => void): (() => void) => {
  const handler = (e: Event) => {
    // StorageEvent.key is null for localStorage.clear(), which also wipes our
    // key, so only skip events that name a different key.
    if (e instanceof StorageEvent && e.key !== null && e.key !== STORAGE_KEY)
      return;
    listener();
  };
  window.addEventListener("storage", handler);
  window.addEventListener(SAME_TAB_EVENT, handler);
  return () => {
    window.removeEventListener("storage", handler);
    window.removeEventListener(SAME_TAB_EVENT, handler);
  };
};

const getServerSnapshot = (): DefaultDeviceId => null;

export const useDefaultDevice = (): DefaultDeviceId =>
  useSyncExternalStore(subscribe, read, getServerSnapshot);

export const useSetDefaultDevice = (): ((id: DefaultDeviceId) => void) =>
  useCallback(setDefaultDevice, []);
